'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type PopoverProps = {
  /** The visible trigger. It is wrapped in a button, so it must not be interactive itself. */
  trigger: ReactNode;
  /** The trigger button's accessible name. */
  label: string;
  children: ReactNode;
  width?: number;
  /**
   * The panel holds something to press (a link): it is a non-modal dialog, not a tooltip, since a
   * tooltip may hold no interactive content.
   */
  interactive?: boolean;
  /**
   * Lets the trigger shrink inside a narrow cell, so text in it can be cut with an ellipsis. Off,
   * the trigger keeps its content's width.
   */
  fitTrigger?: boolean;
  /** Gap between the trigger and the panel, in px. */
  offset?: number;
  'data-testid'?: string;
};

/** The gap between the trigger and the panel. It is padding, so the pointer crosses no gap. */
const OFFSET_PX = 6;
/** The panel keeps this far from the window's edges. */
const EDGE_PX = 8;

/**
 * DS-GAP: LOCAL STAND-IN for the DS Popover. The DS published a Popover in Figma on 10-15, but
 * @lulalife/design-system 0.30.12 (installed) has none; DS `Tooltip` is hover-only with dark
 * fixed styling, and `Menu variant="popover"` opens on click. When the DS Popover reaches npm,
 * swap the body of this file for it and keep the props: call sites don't change.
 *
 * Opens on hover or keyboard focus of the trigger, stays open while the pointer is over the
 * panel, and closes on leave, blur or Escape. The panel is the trigger's description
 * (`aria-describedby`), so a screen reader reads it with the trigger.
 *
 * The panel is fixed under the trigger and kept inside the window, so a scrolling panel around
 * the trigger can't clip it. It stays in the trigger's place in the page, so Tab reaches a link in
 * it, and the gap above it is its own padding, so the pointer can cross from the tag to the panel.
 * Escape closes it and goes no further, so a slideout around it stays open: the key-up that
 * follows is stopped too, since the work order slideout closes on Escape key-up.
 */
export const Popover = ({
  trigger,
  label,
  children,
  width = 320,
  interactive = false,
  fitTrigger = false,
  offset = OFFSET_PX,
  'data-testid': testId,
}: PopoverProps) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  // Set once the panel is measured: it sits above the trigger, and/or is cut to the room it has.
  const [fit, setFit] = useState<{ above: boolean; maxHeight: number | null }>({
    above: false,
    maxHeight: null,
  });
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  // The Escape that closed the panel: its key-up goes no further either.
  const swallowEscapeUp = useRef(false);

  // Where the panel should sit on screen, and whether the slideout correction below has run.
  const target = useRef<{ top: number; left: number } | null>(null);
  // The trigger's top and bottom edges, for the room above and below it.
  const triggerEdges = useRef<{ top: number; bottom: number }>({ top: 0, bottom: 0 });
  const corrected = useRef(false);

  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const right = Math.min(rect.right, window.innerWidth - EDGE_PX);
    target.current = { top: rect.bottom, left: Math.max(EDGE_PX, right - width) };
    triggerEdges.current = { top: rect.top, bottom: rect.bottom };
    corrected.current = false;
    setFit({ above: false, maxHeight: null });
    setPosition(target.current);
  }, [open, width]);

  // Inside a transformed ancestor (the work order slideout, a reactstrap Offcanvas, keeps a
  // `transform` once open), `fixed` is relative to that ancestor, not the window. Measure where the
  // panel landed and shift it once by the difference, so it sits under the trigger either way.
  useLayoutEffect(() => {
    const layer = panelRef.current;
    if (!layer || !position || !target.current || corrected.current) return;
    corrected.current = true;
    const landed = layer.getBoundingClientRect();
    const dx = landed.left - target.current.left;
    const dy = landed.top - target.current.top;
    // Keep it inside the window's height: under the trigger when it fits there; else on the side with
    // more room (above the trigger), cut to that room, with the panel scrolling inside.
    const roomBelow = window.innerHeight - triggerEdges.current.bottom - EDGE_PX;
    const roomAbove = triggerEdges.current.top - EDGE_PX;
    const above = landed.height > roomBelow && roomAbove > roomBelow;
    const room = above ? roomAbove : roomBelow;
    const maxHeight = landed.height > room ? Math.max(room, 0) : null;
    const top = above
      ? triggerEdges.current.top - (maxHeight ?? landed.height)
      : target.current.top;
    setFit({ above, maxHeight });
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && !above) return;
    setPosition({ top: top - dy, left: target.current.left - dx });
  }, [position]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Window capture runs first: nothing behind the popover (a slideout's Escape) sees it.
      event.stopPropagation();
      swallowEscapeUp.current = true;
      close();
    };
    // Fixed under the trigger: a scroll would leave it behind, so it closes instead.
    const onScroll = (event: Event) => {
      if (!panelRef.current?.contains(event.target as Node | null)) close();
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  // Outlives `open`: the key-up comes after the key-down has closed the panel.
  useEffect(() => {
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !swallowEscapeUp.current) return;
      swallowEscapeUp.current = false;
      event.stopPropagation();
    };
    // A key-up lost to the window losing focus mid-press: the next Escape is a new one.
    const onWindowBlur = () => {
      swallowEscapeUp.current = false;
    };
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, []);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover and focus-out on the trigger and panel together; the trigger itself is a button
    <div
      ref={rootRef}
      className={`relative inline-flex${fitTrigger ? ' max-w-full min-w-0' : ''}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={close}
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) close();
      }}
      data-testid={testId}
    >
      <button
        type="button"
        aria-label={label}
        aria-describedby={open ? panelId : undefined}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className={`inline-flex cursor-default rounded-tag-radius border-0 bg-transparent p-space-0${
          fitTrigger ? ' max-w-full min-w-0 text-left' : ''
        }`}
        onFocus={() => setOpen(true)}
      >
        {trigger}
      </button>
      {open && position && (
        <div
          ref={panelRef}
          className="fixed z-tooltip flex flex-col"
          style={{
            ...position,
            width,
            ...(fit.above ? { paddingBottom: offset } : { paddingTop: offset }),
            ...(fit.maxHeight === null ? {} : { maxHeight: fit.maxHeight }),
          }}
        >
          <div
            id={panelId}
            {...(interactive ? { role: 'dialog', 'aria-label': label } : { role: 'tooltip' })}
            className="flex min-h-0 flex-col gap-space-8 overflow-y-auto rounded-lg border border-solid border-border-subtle bg-bg-surface p-space-16 text-left shadow-md"
            data-testid={testId ? `${testId}.panel` : undefined}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export default Popover;
